import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { TerminalViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { ShopAutoComplete, PrinterAutoComplete, CardTerminalAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const TerminalEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = null } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(TerminalViewModel) | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .get<TerminalViewModel>(`/settingsApi/GetTerminal?id=${id}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultModel: TerminalViewModel & { originalValues: Partial<TerminalViewModel>; hasChanges: boolean } = {
        createNew: true,
        active: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultModel.originalValues = { ...defaultModel };
      setViewModel(defaultModel);
    }
  }, [id, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;

    setIsLoading(true);
    try {
      const response = await authService.post<TerminalViewModel>("/settingsApi/CreateOrUpdateTerminal", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/terminal/edit", params: { id: response.id } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof TerminalViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as TerminalViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/terminal/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteTerminal`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/terminal/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryEntity"), "warning");
      return;
    }
    if (viewModel?.hasChanges) showHasChangesError();
    setIsLoading(true);
    try {
      var response = await authService.post<TerminalViewModel>(`/settingsApi/CopyTerminal`, { ...viewModel });
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.entityCopied"), "success");

      navigate({ path: "/settings/terminal/edit", params: { id: response.id } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyFailed")} : ${error.response?.data ?? error.message}`, "error");
      setViewModel(prev => ({ ...prev, newCode: null }));

    } finally {
      setIsLoading(false);
    }
  };




  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.shopNo || viewModel.shopNo.trim() === "") {
      showSnackbar(t("dialog.shopRequired"), "warning")
      return false;
    }



    return true;
  };

  const url = `https://mpos.bexonline.net/apiKey=${viewModel?.apiKey ?? ""}&apiSecret=${viewModel?.apiSecret ?? ""}`;

  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.terminal")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onDelete={handleDelete}
            onCopy={handleCopy}

          />
        </Box>

        {viewModel && (
          <>
            <Grid container spacing={2}>
              {viewModel?.createNew !== true && (() => {
                const url = `https://mpos.bexonline.net/apiKey=${viewModel?.apiKey ?? ""}&apiSecret=${viewModel?.apiSecret ?? ""}`;
                return (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        label={t("common.terminalNo")}
                        value={viewModel.terminalNo || ""}
                        fullWidth
                        margin="normal"
                        disabled
                      />
                    </Grid>


                    <Grid item xs={12} style={{ textAlign: "center", marginTop: 20 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("common.scanOrClickToSetup")}
                      </Typography>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <QRCodeCanvas value={url} size={128} />
                      </a>

                    </Grid>
                  </>
                );
              })()}

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <ShopAutoComplete
                    key={viewModel.shopNo || undefined}
                    valueId={viewModel.shopNo || null}
                    onChange={(selected) => handleChange("shopNo", selected?.value || "")}
                    infoText={t("help.terminalShop")}
                    disabled={viewModel.createNew != true}
                    error={!viewModel.shopNo || viewModel.shopNo.trim() === ""}
                    helperText={!viewModel.shopNo || viewModel.shopNo.trim() === "" ? t("dialog.shopRequired") : ""}

                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.active} onChange={(e) => handleChange("active", e.target.checked)} />}
                  label={t("common.active")}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.cashdrawer} onChange={(e) => handleChange("cashdrawer", e.target.checked)} />}
                  label={t("common.cashDrawer")}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.selfCheckout} onChange={(e) => handleChange("selfCheckout", e.target.checked)} />}
                  label="Self Checkout"
                />
              </Grid>



              {viewModel?.createNew !== true && (
                <>


                  <Grid item xs={12}>
                    <TextField
                      label={t("common.ccuId")}
                      value={viewModel.ccuRegisterID || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.ccuUnit")}
                      value={viewModel.ccuVerificationID || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.apiKey")}
                      value={viewModel.apiKey || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.apiSecret")}
                      value={viewModel.apiSecret || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.installedOnUnit")}
                      value={viewModel.claimedByDevice || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={t("common.installationDate")}
                      value={viewModel.claimedAtDate?.split("T")[0] || ""}

                      fullWidth
                      margin="normal"
                      disabled

                    />
                  </Grid>
                </>
              )}
              {viewModel?.createNew !== true && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                    label={t("common.showSettings")}
                  />
                </Grid>
              )}
              {(showSettings || viewModel.createNew) && (
                <>
                  <Box
                    sx={{
                      backgroundColor: viewModel.createNew ? "transparent" : "secondary.main",
                      padding: 2,
                      borderRadius: 2,
                      width: "100%"
                    }}
                  >

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <CardTerminalAutoComplete
                          key={viewModel.cardTerminalId || undefined}
                          valueId={viewModel.cardTerminalId || null}
                          onChange={(selected) => handleChange("cardTerminalId", selected?.value || "")}
                          infoText={t("help.cardTerminal")}

                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <PrinterAutoComplete
                          key={viewModel.thermalPrinterId || undefined}
                          valueId={viewModel.thermalPrinterId || null}
                          onChange={(selected) => handleChange("thermalPrinterId", selected?.value || "")}
                          label={t("common.thermalPrinter")}
                          infoText={t("help.thermalPrinter")}

                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <PrinterAutoComplete
                          key={viewModel.a4PrinterId || undefined}
                          valueId={viewModel.a4PrinterId || null}
                          onChange={(selected) => handleChange("a4PrinterId", selected?.value || "")}
                          label={t("common.a4Printer")}
                          infoText={t("help.a4Printer")}

                        />
                      </FormControl>
                    </Grid>
                  </Box>
                </>
              )}
            </Grid>
          </>
        )}
        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>


    </>
  );
};

export default TerminalEditPage;
